import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { addBusinessDays, isBefore, isToday, isWeekend } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'throttle-debounce'

import {
  InfoMessage,
  Input,
  InputDatePicker,
  InputLabel,
} from '~shared-components/components'
import {
  LocationDropdown,
  LocationDropdownOption,
  toLocationDropdownOptions,
} from '~shared-components/components/Dropdown/LocationDropdown'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { useUom } from '~shared-components/hooks/useUom'
import { BaseCartProduct } from '~shared-components/model'
import { CustomerLocation } from '~shared-components/transforms/user.transforms'
import {
  formatDate,
  locationToString,
  trimLeadingZeros,
} from '~shared-components/utils/misc'
import { getMinimumOrderQuantity } from '~shared-components/utils/price-utils'

import { GenericModalFooter } from './GenericModalFooter'
import { ModalCloseButton } from './ModalCloseButton'

interface RequestQuoteModalProps {
  customerNumber: string
  isOpen: boolean
  locations: CustomerLocation[]
  onClose: () => void
  product: BaseCartProduct | undefined
  shipToLocationId: string | undefined
}

export const RequestQuoteModal = ({
  customerNumber,
  isOpen,
  locations,
  onClose,
  product,
  shipToLocationId,
}: RequestQuoteModalProps) => {
  const t = useTranslations()
  const u = useUom()
  const [quantity, setQuantity] = useState<string>('')
  const [orderAmount, setOrderAmount] = useState<string>('')
  const [quantityTouched, setQuantityTouched] = useState<boolean>(false)
  const [orderAmountTouched, setOrderAmountTouched] = useState<boolean>(false)
  const nextBusinessDay = useMemo(() => addBusinessDays(new Date(), 1), [])
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(
    nextBusinessDay,
  )
  const [shipToId, setShipToId] = useState<string | undefined>(shipToLocationId)
  const quantityNumber = Number.parseInt(quantity ?? '1', 10)
  const orderAmountNumber = Number.parseFloat(orderAmount ?? '1')
  const originalQuantityRef = useRef<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isAmountRecalcRequired, setIsAmountRecalcRequired] = useState<boolean>(
    false,
  )
  const [showRoundingMessage, setShowRoundingMessage] = useState<boolean>(false)
  const productPackagingSizeQuantity =
    product?.data.packaging.size.quantity ?? 1

  const updateOrderAmount = useCallback(
    async (quantity: number | undefined) => {
      setIsLoading(true)

      const orderAmount = await new Promise(resolve =>
        resolve(
          Math.round(productPackagingSizeQuantity * (quantity ?? 1) * 100) /
            100,
        ),
      )

      setOrderAmount(String(orderAmount))
      setIsAmountRecalcRequired(false)

      setIsLoading(false)
    },
    [productPackagingSizeQuantity],
  )

  const minimumOrderQuantity = product
    ? getMinimumOrderQuantity(product.pricePoints, product.data.packaging)
    : undefined

  const updateQuantity = useCallback(
    (quantityToQuery: number | undefined) =>
      quantityToQuery && updateOrderAmount(quantityToQuery),
    [updateOrderAmount],
  )

  useEffect(() => {
    if (isOpen) {
      updateQuantity(
        product?.quantity !== undefined
          ? product.quantity
          : minimumOrderQuantity ?? 1,
      )
      originalQuantityRef.current = String(product?.quantity)
      setQuantity(String(product?.quantity ?? 1))
    }
    return () => {
      setQuantity('1')
      setOrderAmount('1')
      setDeliveryDate(nextBusinessDay)
      setShowRoundingMessage(false)
    }
  }, [product, isOpen, updateQuantity, minimumOrderQuantity, nextBusinessDay])

  const onQuantity = (quantity: string) => {
    setQuantity(quantity)
    debounce(500, updateQuantity)(Number(quantity))
  }

  const onQuantityChanged = (quantity: string) => {
    onQuantity(quantity)
    setShowRoundingMessage(false)
    setQuantityTouched(true)
  }

  const onAmountChanged = (amount: string) => {
    setOrderAmount(amount)
    setIsAmountRecalcRequired(true)
    setOrderAmountTouched(true)
  }

  const amountUnit = u(product?.data.packaging.size?.uom)
  const quantityUnit = amountUnit
    ? `${productPackagingSizeQuantity} ${amountUnit} ${product?.data.packaging.type}`
    : ''

  const onFooterActionClick = () => {
    const shipToLocation = locations.find(location => location.id === shipToId)
    const address = locationToString(shipToLocation)

    let replyText = t('requestquote.request')
      .replace('@@CUSTOMER_NUMBER@@', trimLeadingZeros(customerNumber)!)
      .replace('@@QUANTITY@@', `${quantityNumber} ${quantityUnit}`)
      .replace('@@REQUESTED_AMOUNT@@', `${orderAmount} ${amountUnit}`)
      .replace('@@SHIP_TO_ADDRESS@@', address)
      .replace('@@REQUESTED_DELIVERY_DATE@@', formatDate(deliveryDate!))

    if (product) {
      replyText = replyText
        .replace('@@PRODUCT_NAME@@', product.data.name)
        .replace('@@PRODUCT_NUMBER@@', trimLeadingZeros(product.data.sourceId)!)
    } else {
      Sentry.captureMessage(
        'Failed to inject product name and number on the request quote message',
        {
          extra: {
            orderAmount,
            product,
            quantityNumber,
            shipToId,
          },
        },
      )
    }

    // BCA-584: FreshChat removed - quote requests now logged
    // In production, this should be replaced with an API call or email
    // eslint-disable-next-line no-console
    console.log('Quote request:', replyText)
    onClose()
  }

  const onFooterCalculateClick = () => {
    if (orderAmountNumber) {
      const newQuantity = Math.ceil(
        Math.floor((orderAmountNumber / productPackagingSizeQuantity) * 1e7) /
          1e7,
      ) // fixes js rounding error 0.1 + 0.2 = 0.300000000004
      setShowRoundingMessage(
        Boolean(newQuantity) &&
          Math.abs(
            orderAmountNumber - newQuantity * productPackagingSizeQuantity,
          ) > 1e-7,
      )
      onQuantity(String(newQuantity))
    }
  }

  const quantityError =
    quantityNumber <= 0
      ? t('label.quantity.zero')
      : minimumOrderQuantity !== undefined &&
        minimumOrderQuantity > 1 &&
        quantityNumber < minimumOrderQuantity
      ? t('label.quantity.minimumorderquantity').replace(
          '@@MINIMUMORDERQUANTITY@@',
          `${minimumOrderQuantity}`,
        )
      : quantityNumber > 1e10 - 1
      ? t('label.quantity.toolarge')
      : quantityTouched && quantity === ''
      ? t('field.required').replace('@@VALUE@@', t('label.quantity'))
      : undefined

  const minimumOrderAmount = product
    ? (minimumOrderQuantity ?? 0) * productPackagingSizeQuantity
    : 0

  const amountError =
    orderAmountNumber <= 0
      ? t('label.quantity.zero')
      : minimumOrderQuantity !== undefined &&
        minimumOrderQuantity > 1 &&
        orderAmountNumber < minimumOrderAmount
      ? t('label.quantity.minimumorderamount').replace(
          '@@MINIMUMORDERAMOUNT@@',
          `${minimumOrderAmount}`,
        )
      : orderAmountNumber > 1e10 - 1
      ? t('label.quantity.toolarge')
      : orderAmountTouched && orderAmount === ''
      ? t('field.required').replace('@@VALUE@@', t('label.requestedamount'))
      : undefined

  const dateError =
    deliveryDate === undefined
      ? t('field.required').replace(
          '@@VALUE@@',
          t('label.requesteddeliverydate'),
        )
      : undefined

  const disableActionButton =
    isLoading ||
    Boolean(quantityError) ||
    Boolean(amountError) ||
    Boolean(dateError)

  const handleLocationChange = ({ id }: CustomerLocation) => {
    setShipToId(id)
  }

  const handleDateError = () => setDeliveryDate(undefined)

  // no useMemo necessary as they are always re-built in the selector
  const sortedLocationOptions: LocationDropdownOption[] = toLocationDropdownOptions(
    locations,
  )

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      motionPreset="scale"
      onClose={onClose}
      size="3xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading
            color="deepPurple"
            size="xl"
            textAlign="left"
            textTransform="capitalize"
          >
            {product?.data?.name}
          </Heading>
        </ModalHeader>
        <ModalCloseButton aria-label="close" onClose={onClose} />
        <ModalBody>
          <Box pb={5}>
            <Text fontWeight="bold">{t('label.orderdetails')}</Text>
          </Box>
          <Stack direction={['column', 'column', 'row']} spacing={5}>
            <VStack alignItems="unset" justify="stretch" spacing={5} w="100%">
              <Flex direction="column">
                <InputLabel required>{t('label.quantity')}</InputLabel>
                <Input
                  data-testid="request-quote-input-quantity"
                  error={Boolean(quantityError)}
                  helper={quantityError}
                  onValueChange={onQuantityChanged}
                  pattern="^\d+$"
                  type="text"
                  unit={quantityUnit}
                  value={quantity}
                />
              </Flex>

              <Flex direction="column">
                <InputLabel required>{t('label.requestedamount')}</InputLabel>
                <Input
                  data-testid="request-quote-input-amount"
                  error={Boolean(amountError)}
                  helper={amountError}
                  isDisabled={isLoading}
                  onBlur={onFooterCalculateClick}
                  onValueChange={onAmountChanged}
                  pattern="^\d+(\.\d{0,3})?$"
                  type="text"
                  unit={amountUnit}
                  value={orderAmount}
                />
              </Flex>

              <Box h={[null, null, '60px']} maxW={[null, null, '350px']}>
                {showRoundingMessage && (
                  <InfoMessage fullWidth narrow type="info">
                    {t('cart.recalculate.rounding')}
                  </InfoMessage>
                )}
              </Box>
            </VStack>
            <VStack alignItems="unset" justify="stretch" spacing={5} w="100%">
              <Flex direction="column">
                <Flex
                  justify="flex-start"
                  sx={{
                    '&:hover': {
                      svg: {
                        fill: 'neutral.90',
                      },
                    },
                    svg: {
                      transition: 'fill 0.2s ease-in',
                    },
                  }}
                >
                  <InputLabel required>
                    {t('label.requesteddeliverydate')}
                  </InputLabel>
                </Flex>
                <InputDatePicker
                  dateFormatter={formatDate}
                  error={Boolean(dateError)}
                  helper={dateError}
                  isDateBlocked={date =>
                    isWeekend(date) ||
                    isToday(date) ||
                    isBefore(date, new Date())
                  }
                  onDateChange={newDate => {
                    setDeliveryDate(newDate)
                  }}
                  onError={handleDateError}
                  placeholder="dd-mm-yyyy"
                  value={deliveryDate?.toString()}
                />
              </Flex>
              <Flex direction="column">
                <InputLabel required>{t('label.shiptoaddress')}</InputLabel>
                <LocationDropdown
                  data-testid="ship-to-dropdown"
                  onLocationChange={handleLocationChange}
                  options={sortedLocationOptions}
                  value={shipToId}
                />
              </Flex>
            </VStack>
          </Stack>
        </ModalBody>
        <ModalFooter justifyContent="center">
          <GenericModalFooter
            actionButtonId="modal-request-quote-btn"
            actionIcon="IconCheckmark"
            actionTextLabel="cart.requestquote"
            isActionDisabled={disableActionButton}
            isLoadingAction={isLoading}
            isReactionRequired={isAmountRecalcRequired}
            onAction={onFooterActionClick}
            onCancel={onClose}
            onReaction={onFooterCalculateClick}
            reactionButtonId="modal-calculate-btn"
            reactionIcon="IconSpinner"
            reactionTextLabel="cart.recalculate"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
